import { useState, useMemo } from "react";
import { formatINR } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";
import { useApp } from "@/lib/appContext";
import { stateName } from "@/lib/states";
import EduSection from "@/components/EduSection";

// State-specific stamp duty rates (typical, non-agri). Format: {stamp:{male,female,joint}, reg}
const STATE_RATES = {
  UP: { male: 0.07,  female: 0.06,  joint: 0.065, reg: 0.01, femaleRebate: 10000 },
  DL: { male: 0.06,  female: 0.04,  joint: 0.05,  reg: 0.01 },
  HR: { male: 0.07,  female: 0.05,  joint: 0.06,  reg: 0.01 },
  MH: { male: 0.06,  female: 0.05,  joint: 0.055, reg: 0.01 },
  KA: { male: 0.05,  female: 0.05,  joint: 0.05,  reg: 0.01 },
  TN: { male: 0.07,  female: 0.07,  joint: 0.07,  reg: 0.04 },
  WB: { male: 0.06,  female: 0.06,  joint: 0.06,  reg: 0.01 },
  GJ: { male: 0.049, female: 0.049, joint: 0.049, reg: 0.01 },
  RJ: { male: 0.06,  female: 0.05,  joint: 0.055, reg: 0.01 },
  MP: { male: 0.075, female: 0.065, joint: 0.07,  reg: 0.03 },
  PB: { male: 0.07,  female: 0.05,  joint: 0.06,  reg: 0.01 },
  TG: { male: 0.075, female: 0.075, joint: 0.075, reg: 0.005 },
  AP: { male: 0.075, female: 0.075, joint: 0.075, reg: 0.005 },
  KL: { male: 0.08,  female: 0.08,  joint: 0.08,  reg: 0.02 },
  BR: { male: 0.06,  female: 0.058, joint: 0.06,  reg: 0.02 },
  DEFAULT: { male: 0.06, female: 0.05, joint: 0.055, reg: 0.01 },
};

export default function StampDuty() {
  const { state } = useApp();
  const [buyer, setBuyer] = useState("male");
  const [value, setValue] = useState("5000000");

  const rates = STATE_RATES[state] || STATE_RATES.DEFAULT;
  const { stamp, reg, total } = useMemo(() => {
    const v = Number(value) || 0;
    let stampAmt = v * rates[buyer];
    if (buyer === "female" && rates.femaleRebate) stampAmt = Math.max(0, stampAmt - rates.femaleRebate);
    const regAmt = v * rates.reg;
    return { stamp: stampAmt, reg: regAmt, total: stampAmt + regAmt };
  }, [buyer, value, rates]);

  const rows = [
    ["State", stateName(state)],
    ["Property Value", formatINR(Number(value) || 0)],
    ["Buyer Type", buyer[0].toUpperCase()+buyer.slice(1)],
    ["Stamp Duty Rate", `${(rates[buyer] * 100).toFixed(2)}%`],
    ["Registration Rate", `${(rates.reg * 100).toFixed(2)}%`],
    ["Stamp Duty", formatINR(stamp)],
    ["Registration Charges", formatINR(reg)],
    ["Total Payable", formatINR(total)],
  ];

  return (
    <div>
      <CalcPageHeader title="Stamp Duty Calculator" description={`Estimate stamp duty & registration charges — ${stateName(state)}.`} slug="stamp-duty" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <Field label="Property Value (₹)">
              <input data-testid="stamp-value-input" type="number" value={value} onChange={(e)=>setValue(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" />
            </Field>
            <Field label="Buyer type">
              <div className="grid grid-cols-3 gap-2" data-testid="buyer-type-group">
                {["male","female","joint"].map((k) => (
                  <button key={k} data-testid={`buyer-${k}`} onClick={()=>setBuyer(k)}
                    className={`h-11 px-3 rounded-md text-sm font-medium border transition capitalize ${buyer===k ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:bg-accent"}`}>{k}</button>
                ))}
              </div>
            </Field>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel
            title={`Stamp Duty · ${stateName(state)}`}
            slug="stamp-duty"
            highlight={{ label: "Total Payable", value: formatINR(total), sub: `Stamp ${formatINR(stamp)} + Reg ${formatINR(reg)}` }}
            rows={rows}
            note={`Rates typical for ${stateName(state)}. Some states offer women/joint rebates. Verify with your sub-registrar.`}
            onReset={()=>{setValue("");setBuyer("male");}}
          />
        </div>
      </div>
      <EduSection
        howItWorks={<p>Stamp duty is a state subject in India — rates differ dramatically. LandCalc uses the correct rate for your selected state. Total payable = (Property Value × Stamp %) + (Property Value × Registration %) minus any state-specific women rebate.</p>}
        faqs={[{q:"Is stamp duty same across India?",a:"No — each state sets its own rate. Change your state in the header to see local rates."},{q:"Do women get lower stamp duty?",a:"Yes in many states (Delhi, Haryana, Punjab, Rajasthan, UP). Others treat all buyers equally."}]}
        related={["registry","loan-emi","land-converter"]}
      />
    </div>
  );
}
